import { Component } from '@theme/component';

/**
 * Minimal fade slider for the Arvana testimonials section.
 * All slides share one grid cell; only the current one is visible and interactive.
 * Optional autoplay (`autoplay="<seconds>"`) pauses on hover, focus and hidden tabs, and is
 * disabled in the theme editor and for reduced-motion users.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [status] - Visually hidden live region announcing "n of total".
 *
 * @extends {Component<Refs>}
 */
class ArvanaSliderComponent extends Component {
  requiredRefs = ['slides'];

  #current = 0;

  /** @type {number | undefined} */
  #timer;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.#stop() : this.#start()), { signal });
    this.addEventListener('pointerenter', () => this.#stop(), { signal });
    this.addEventListener('pointerleave', () => this.#start(), { signal });
    this.addEventListener('focusin', () => this.#stop(), { signal });
    this.addEventListener('focusout', () => this.#start(), { signal });

    this.#current = Math.max(0, this.refs.slides.findIndex((slide) => !slide.hasAttribute('inert')));
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.goTo(Math.min(this.#current, this.refs.slides.length - 1), { announce: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#abortController?.abort();
  }

  /** Shows the next slide (`on:click="/next"`). */
  next() {
    this.goTo(this.#current + 1);
  }

  /** Shows the previous slide (`on:click="/previous"`). */
  previous() {
    this.goTo(this.#current - 1);
  }

  /**
   * Shows the slide at `index`, wrapping around at both ends.
   * @param {number} index
   * @param {{ announce?: boolean }} [options]
   */
  goTo(index, { announce = true } = {}) {
    const { slides, status } = this.refs;
    const total = slides.length;
    if (total === 0) return;

    this.#current = ((index % total) + total) % total;

    for (const [i, slide] of slides.entries()) {
      const active = i === this.#current;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.classList.toggle('is-active', active);
    }

    if (announce && status && !this.#timer) {
      status.textContent = status.dataset.template
        ?.replace('[current]', String(this.#current + 1))
        .replace('[total]', String(total)) ?? '';
    }
  }

  get #autoplayInterval() {
    const seconds = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(seconds) || seconds <= 0 ? 0 : seconds * 1000;
  }

  #start() {
    this.#stop();

    const interval = this.#autoplayInterval;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!interval || reduceMotion || window.Shopify?.designMode || this.refs.slides.length < 2) return;

    this.#timer = window.setInterval(() => this.goTo(this.#current + 1, { announce: false }), interval);
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const index = this.refs.slides.findIndex((slide) => slide.contains(/** @type {Node} */ (event.target)));
    if (index !== -1) this.goTo(index, { announce: false });
  };
}

if (!customElements.get('arvana-slider-component')) {
  customElements.define('arvana-slider-component', ArvanaSliderComponent);
}
